import { env, Release } from '@/env';
import { ImageDataCache } from '@/modules/ssp_database/ObjectProfileModule/profile_loader/image_data_cache';
import { ICoordPair } from "@/modules/ToolModule/TwoPointsPicker";
import { assert } from '@/utils/error';
import { downloadTextData } from '@/utils/file';
import { linspace, V3 } from "@/utils/math";
import { deg2rad, rad2deg } from '@hscmap/angle';
import { Fits } from '@hscmap/fits';
import { EquatorialCoord, tile } from "@hscmap/stellar-globe";
import axios from 'axios';
import { mat4, vec4, vec3 } from "gl-matrix";
import { intersect } from "./intersect";
interface ITractRerun { tract: number, rerun: string }

const PIXEL_SCALE = deg2rad(0.168 / 3600) // 1pix ~ 0.168 arcsec

export async function laodFromTile(line: ICoordPair, n: number, rerun: string, filter: string) {
  const baseUrl = env.hscMapData(rerun)
  const { data: tracts } = await axios.get(`${baseUrl}/meta.json`)

  const probes = linspace3(line, n)
  const profile = probes.map((p) => NaN)

  const level = tileLevel(line)
  await Promise.all(Object.keys(tracts).map((tractName) => {
    const tract = tile.Tract.fromFitsHeader(tracts[tractName].wcs)
    const picker = new TractTilePixelPicker(tract, `${baseUrl}/${filter}/${tractName}`)
    if (picker.intersect(line)) {
      return picker.pickFromTile(level, probes, profile)
    }
  }).filter((p) => p))
  return profile
}

export async function loadFromFits(line: ICoordPair, release: Release, rerun0: string, filter: string) {
  const lineLength = angle(line)
  const n = lineLength / PIXEL_SCALE
  const probes = linspace3(line, n)
  const { c0, c1 } = line
  // tslint:disable-next-line:max-line-length
  const infoUrl = `${release.cutoutUrl}?ra1=${c0.a.deg}&dec1=${c0.d.deg}&ra2=${c1.a.deg}&dec2=${c1.d.deg}&filter=${filter}&type=info`
  const { data: tractRereuns } = await axios.get<ITractRerun[]>(infoUrl)
  const profile = probes.map((p, i) => NaN)

  if (tractRereuns.length === 0) {
    throw new Error('No available data.')
  } else {
    const { tract: tractName, rerun } = tractRereuns[0]
    // tslint:disable-next-line:max-line-length
    const imageUrl = `${release.cutoutUrl}?ra1=${c0.a.deg}&dec1=${c0.d.deg}&ra2=${c1.a.deg}&dec2=${c1.d.deg}&type=coadd&image=on&filter=${filter}&tract=${tractName}&rerun=${rerun}`
    const hdul = await Fits.fetch(imageUrl)
    const hdu = hdul[1]
    assert(hdu.card('BITPIX', 'number') === -32) // BITPIX = -32 => float32
    const tract = tile.Tract.fromFitsHeader(hdu.header)
    const naxis1 = hdu.card('NAXIS1', 'number')
    const data = hdu.float32array()
    for (let i = 0; i < probes.length; ++i) {
      const p = probes[i]
      let [x, y] = tract.xyz2pixel(p)
      x = Math.floor(x)
      y = Math.floor(y)
      profile[i] = data[y * naxis1 + x] / tract.pix2jacobian(x, y)
    }
    const header: any[] = [
      `# ${rad2deg(lineLength) * 3600} arcsec`,
      `# c0: ${coord2str(c0)}`,
      `# c1: ${coord2str(c1)}`,
    ]
    downloadTextData('profile.txt', 'text/plain', header.concat(profile).join('\n'))
  }
  return profile
}

function coord2str(coord: EquatorialCoord) {
  const { a, d } = coord.toString()
  return `a=${a} d=${d}`
}

function angle(line: ICoordPair) {
  const { c0, c1 } = line
  return vec3.angle(c0.xyz, c1.xyz)
}

function decodePng(z: number) {
  //   a = 10000.,
  //   b = -1/8;
  //
  //   = pack =
  //   y = asinh(a * x) / asinh(a) - b / (1 - b);
  //
  //   = unpack =
  //   x = 1/a * sinh((y + b/(1-b))*asinh(a))
  const y = z / 255
  const vmax = 10 ** (0.4 * 8)
  const a = 10000
  const b = -1. / 8.
  const x = 1 / a * Math.sinh((y + b / (1 - b)) * Math.asinh(a))
  return vmax * x
}

function tileLevel(line: ICoordPair) {
  const a = vec3.angle(line.c0.xyz, line.c1.xyz)
  const pixelSize = PIXEL_SCALE
  const ntile = a / pixelSize / 256
  const level = Math.max(0, Math.floor(Math.log(ntile) / Math.LN2))
  return level
}

const cache = new ImageDataCache()

class TractTilePixelPicker {
  private invM: mat4 // world 2 pixel
  private crval: V3

  constructor(readonly tract: tile.Tract, readonly baseUrl: string) {
    this.invM = mat4.invert(mat4.create(), tract.mMatrix)!
    this.crval = EquatorialCoord.fromRad(tract.crval1, tract.crval2).xyz
  }

  async pickFromTile(level: number, probes: V3[], profile: number[]) {
    for (let i = 0; i < probes.length; ++i) {
      const p = probes[i]
      const [x, y] = this.world2pixel(p);
      if (x < 0 || x >= this.tract.naxis1 || y < 0 || y >= this.tract.naxis2) {
        continue
      }
      const patchX = Math.floor((x >> level) / this.tract.tileSize)
      const patchY = Math.floor((y >> level) / this.tract.tileSize)
      try {
        const url = `${this.baseUrl}/${level}/${patchY}/${patchX}.png`
        const imageData = await cache.get(url)
        const xx = Math.floor(x >> level) % this.tract.tileSize
        const yy = 255 - Math.floor(y >> level) % this.tract.tileSize
        const v = imageData.data[(yy * this.tract.tileSize + xx) << 2]
        profile[i] = decodePng(v) / this.tract.pix2jacobian(x, y)
      } catch (e) {
        console.error(e)
      }
    }
  }

  world2pixel(xyz: V3) {
    const sep = vec3.angle(xyz, this.crval)
    const r = 1 / Math.cos(sep)
    const [x, y, z] = xyz
    const pixel = vec4.transformMat4(vec4.create(), [r * x, r * y, r * z, 1], this.invM)
    return [pixel[0], pixel[1]]
  }

  pixel2world(x: number, y: number): V3 {
    const p = vec4.transformMat4(vec4.create(), [x, y, 0, 1], this.tract.mMatrix)
    return [p[0], p[1], p[2]]
  }

  intersect(line: ICoordPair) {
    const a = this.pixel2world(0, 0)
    const b = this.pixel2world(this.tract.naxis1, 0)
    const c = this.pixel2world(this.tract.naxis1, this.tract.naxis2)
    const d = this.pixel2world(0, this.tract.naxis2)
    return intersect(line, a, b, c) || intersect(line, a, c, d)
  }
}

function linspace3(line: ICoordPair, n: number, end = true) {
  const xyz0 = line.c0.xyz
  const xyz1 = line.c1.xyz
  const xs = linspace(xyz0[0], xyz1[0], n, end)
  const ys = linspace(xyz0[1], xyz1[1], n, end)
  const zs = linspace(xyz0[2], xyz1[2], n, end)
  const a: V3[] = []
  for (let i = 0; i < xs.length; ++i) {
    a.push([xs[i], ys[i], zs[i]])
  }
  return a
}
